import AddCircle from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRenderCellParams,
  GridToolbar
} from '@mui/x-data-grid-pro'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { useEditInventory } from '../../components/Inventory/useEditInventory/useEditInventory'
import { useEditInventoryInstance } from '../../components/Inventory/useEditInventoryInstance/useEditInventoryInstance'
import InventoryPaginationHeader from '../../components/InventoryPaginationHeader/InventoryPaginationHeader'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import useGetInventoryPages from '../../hooks/useGetInventoryPages/useGetInventoryPages'
import { AppRoutes } from '../../models'
import { LabelsInventoryParts } from '../../models/Labels'
import { InventoryContext } from '../../providers/InventoryProvider/InventoryProvider'
import { useUpdateInventoryPartMutation } from '../../redux/api'
import { InventoryPartEntry } from '../../sdk'
import {
  getPartAvailableQuantity,
  getPartInShopQuantity,
  getPartPendingClaimedQuantity,
  getPartPendingReturnQuantity,
  getPartReceivedQuantity
} from '../../utils/convertFluidsUnits/convertFluidsUnits'
import { formatCurrencyInDataGrid } from '../../utils/formatCurrencyInDataGrid'

export const InventoryTable = () => {
  const { accessToken } = useAccessTokenAndShopId()
  const { view } = useContext(InventoryContext)
  const {
    shopInventory,
    isLoading,
    autoGetNextPage,
    reachedEnd,
    getPage,
    getAllPages,
    cancelGetAllPages
  } = useGetInventoryPages({
    stockedOnly: view === 'stocked',
    fluidsOnly: view === 'fluids'
  })

  const [deleteProps, setDeleteProps] = useState<InventoryPartEntry>()
  const { setCustomProps: setEditInventoryProps, EditInventoryDialog } =
    useEditInventory()
  const {
    setCustomProps: setAddInventoryInstanceProps,
    EditInventoryInstanceDialog
  } = useEditInventoryInstance()
  const [updateInventoryPart] = useUpdateInventoryPartMutation()
  const handleDelete = useCallback(async () => {
    if (!deleteProps) {
      return
    }
    updateInventoryPart({
      authorization: accessToken,
      inventoryPartEntry: {
        id: deleteProps?.id,
        info: {
          ...deleteProps.info,
          archived: new Date()
        }
      }
    })
    setDeleteProps(undefined)
  }, [accessToken, deleteProps, updateInventoryPart])
  const columns = useMemo(
    () => [
      {
        field: 'partNumber',
        headerName: LabelsInventoryParts.partNumber,
        flex: 2,
        renderCell: (params: GridRenderCellParams) => (
          <Link
            component={RouterLink}
            to={`${AppRoutes.INVENTORY}/${params.id}`}>
            {params.value}
          </Link>
        )
      },
      {
        field: 'name',
        headerName: LabelsInventoryParts.name,
        flex: 2
      },
      {
        field: 'suggestedPricePerUnit',
        headerName: LabelsInventoryParts.suggestedPricePerUnit,
        flex: 1,
        sortable: false,
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'unitOfMeasure',
        headerName: LabelsInventoryParts.unitOfMeasure,
        flex: 1,
        sortable: false
      },
      {
        field: 'brand',
        headerName: LabelsInventoryParts.brand,
        flex: 1
      },
      {
        field: 'received',
        headerName: LabelsInventoryParts.received,
        flex: 1
      },
      {
        field: 'available',
        headerName: LabelsInventoryParts.available,
        flex: 1
      },
      {
        field: 'pendingReturn',
        headerName: LabelsInventoryParts.pendingReturn,
        flex: 1,
        description: 'Parts are marked for return but not returned yet'
      },
      {
        field: 'pendingClaimed',
        headerName: LabelsInventoryParts.pendingClaimed,
        flex: 1,
        description: 'Parts are claimed by an open repair order'
      },
      {
        field: 'inShop',
        headerName: LabelsInventoryParts.inShop,
        flex: 1
      },
      {
        field: 'description',
        headerName: LabelsInventoryParts.description,
        flex: 2
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 2,
        getActions: (params: GridRenderCellParams) => {
          return [
            <GridActionsCellItem
              key="Edit"
              icon={<EditIcon />}
              label="Edit"
              onClick={() => {
                setEditInventoryProps({
                  existingEntry: shopInventory?.allParts![params.id]
                })
              }}
            />,
            <GridActionsCellItem
              key="Add"
              icon={<AddCircle />}
              label="Add"
              onClick={() => {
                if (!shopInventory) {
                  return
                }
                setAddInventoryInstanceProps({
                  partEntry: shopInventory!.allParts![params.id],
                  existingEntry: undefined
                })
              }}
            />,
            <GridActionsCellItem
              key="Delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                setDeleteProps(shopInventory?.allParts![params.id])
              }}
            />
          ]
        }
      }
    ],
    [setAddInventoryInstanceProps, setEditInventoryProps, shopInventory]
  )
  const rows = useMemo(() => {
    if (!shopInventory) {
      return []
    }
    return Object.keys(shopInventory.allParts!).map((id) => {
      const part = shopInventory.allParts![id]
      const received =
        getPartReceivedQuantity(id, shopInventory) +
        (part.info!.isFluids ? ' qt.' : '')
      const available =
        getPartAvailableQuantity(id, shopInventory) +
        (part.info!.isFluids ? ' qt.' : '')
      const pendingReturn =
        getPartPendingReturnQuantity(id, shopInventory) +
        (part.info!.isFluids ? ' qt.' : '')
      const pendingClaimed =
        getPartPendingClaimedQuantity(id, shopInventory) +
        (part.info!.isFluids ? ' qt.' : '')
      const inShop =
        getPartInShopQuantity(id, shopInventory) +
        (part.info!.isFluids ? ' qt.' : '')
      return {
        id,
        ...part.info!,
        received,
        available,
        inShop,
        pendingReturn,
        pendingClaimed
      }
    })
  }, [shopInventory])
  return (
    <>
      <EditInventoryDialog />
      <EditInventoryInstanceDialog />
      <Dialog open={!!deleteProps} onClose={() => setDeleteProps(undefined)}>
        <DialogTitle>Delete Part {deleteProps?.info?.name}</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this part?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProps(undefined)}>Cancel</Button>
          <Button onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
      <InventoryPaginationHeader
        getPage={getPage}
        isLoading={isLoading}
        autoGetNextPage={autoGetNextPage}
        reachedEnd={reachedEnd}
        cancelGetAllPages={cancelGetAllPages}
        getAllPages={getAllPages}
        numPartsLoaded={Object.keys(shopInventory.allParts || {}).length}
        numPartsTotal={shopInventory.totalNumParts || 0}
      />
      <DataGridPro
        disableRowSelectionOnClick
        slots={{
          toolbar: GridToolbar
        }}
        loading={isLoading || (autoGetNextPage && !reachedEnd)}
        density="compact"
        columns={columns}
        rows={rows}
        sx={{ border: '0px' }}
      />
    </>
  )
}
